import { z } from "zod";

/**
 * Mailen vanuit het adresboek (docs/ADRESBOEK.md, "Mailen"). Ontvangers kies je uit het bedrijf: het algemene
 * adres (`company`) of een persoon van het bedrijf (zijn id). Vrije adressen kunnen niet, zodat Klantpunt geen
 * open doorgeefluik voor mail wordt en elke mail bij een bedrijf of persoon in het logboek terechtkomt.
 */
export const MAIL_SUBJECT_MAX = 200;
export const MAIL_BODY_MAX = 20_000;
/** Ontvangers (aan + cc) per mail. */
export const MAIL_RECIPIENTS_MAX = 20;

const recipientKey = z.union([z.literal("company"), z.uuid()]);

export const directoryMailSchema = z
  .object({
    id: z.uuid().optional(),
    /** Bedrijf waarbij de mail hoort; leeg bij een persoon zonder bedrijf. */
    companyId: z.uuid().nullish(),
    /** Gestuurd vanuit het paneel van deze persoon (alleen voor de herkomst). */
    personId: z.uuid().nullish(),
    to: z.array(recipientKey).min(1, "Kies minstens één ontvanger").max(MAIL_RECIPIENTS_MAX),
    cc: z.array(recipientKey).max(MAIL_RECIPIENTS_MAX).default([]),
    /** Waar de mail over gaat; de mail is dan ook bij het gebouw terug te vinden. */
    buildingId: z.uuid().nullish(),
    /** Afspraak uit de planning: de mail krijgt een agenda-uitnodiging (.ics) mee en hoort bij dat gebouw. */
    appointmentId: z.uuid().nullish(),
    subject: z.string().trim().min(1, "Vul een onderwerp in").max(MAIL_SUBJECT_MAX),
    body: z.string().trim().min(1, "Schrijf een bericht").max(MAIL_BODY_MAX),
    /** Kopie (bcc) naar het eigen mailadres. */
    copyToMe: z.boolean().default(false),
  })
  .refine((b) => b.companyId || b.personId, { message: "Kies een bedrijf of persoon", path: ["companyId"] });
export type DirectoryMailBody = z.input<typeof directoryMailSchema>;

export const directoryMailQuerySchema = z
  .object({ companyId: z.uuid().optional(), personId: z.uuid().optional() })
  .refine((q) => q.companyId || q.personId, { message: "companyId of personId ontbreekt" });

export interface MailRecipientDto {
  name: string | null;
  email: string;
  personId: string | null;
}

export interface DirectoryMailDto {
  id: string;
  direction: "out" | "in";
  companyId: string | null;
  to: MailRecipientDto[];
  cc: MailRecipientDto[];
  building: { id: string; name: string } | null;
  subject: string;
  body: string;
  sentBy: { id: string; name: string | null } | null;
  createdAt: string;
}

/** Ontvangers waaruit je kunt kiezen in het venster *Mail sturen*. */
export interface MailRecipientOption {
  key: "company" | string;
  name: string;
  email: string;
  jobTitle: string | null;
}

/**
 * Antwoord op een mail uit het adresboek, voor de Inbox (tabblad *Mailantwoorden*). Het antwoord staat ook in het
 * logboek van het bedrijf of de persoon.
 */
export interface DirectoryReplyDto extends DirectoryMailDto {
  /** Afzender van het antwoord. */
  from: MailRecipientDto;
  company: { id: string; name: string } | null;
  person: { id: string; name: string } | null;
  /** Onderwerp van de mail waarop dit het antwoord is. */
  inReplyTo: { id: string; subject: string; sentBy: string | null } | null;
  readAt: string | null;
}

export interface DirectoryRepliesResponse {
  items: DirectoryReplyDto[];
  unread: number;
}

export const directoryRepliesQuerySchema = z.object({
  status: z.enum(["ongelezen", "alle"]).default("ongelezen"),
});

export const directoryReplyReadSchema = z.object({ read: z.boolean().default(true) });
